import { Grid, Button, CircularProgress, Box } from '@material-ui/core';
import {
  ColoredImageIcon,
  DocumentFileIcon,
  MicrophoneIcon,
  Typography,
  VideoIcon,
} from '@room-match/shared-ui-components';

import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { IOutput } from 'shared/interfaces/IProfile';
import { IOnProgress } from 'shared/interfaces/utils/IAxios';
import { uploadService } from 'shared/services/uploadService';
import { ProgressOverlay, useStyles } from './DropZone.style';
import { COLORS } from 'shared/constants/COLORS';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { FileType } from 'shared/enums/FileType';

interface Props {
  label: string;
  type: 'Images' | 'Videos' | 'Audio' | 'Files';
  file: File | IOutput;
  setToDone: (id: string) => void;
  id: string;
  removeMe: (id: string) => void;
}

const types: any = {
  Images: FileType.IMAGE,
  Videos: FileType.VIDEO,
  Audio: FileType.AUDIO,
  Files: FileType.DOCUMENT,
};

const { uploadMedia } = uploadService();

const UploadingItem: React.FC<Props> = ({ label, type, file, id, setToDone, removeMe }) => {
  const { mutateAsync: uploadMediaMutateAsync, isLoading: uploadMediaMutateLoading } = uploadMedia();
  const [uploading, setuploading] = useState(false);
  const [progress, setprogress] = useState(100);
  const classes = useStyles();
  const queryClient = useQueryClient();

  const onProgres: IOnProgress = (progress: number) => {
    setprogress(progress);
  };

  const handleUpload = async (data: File | any, image: IOutput | any) => {
    setuploading(true);
    const formData = new FormData();

    if (type === 'Images') {
      const {
        attachment,
        file_name,
        file_width,
        file_height,
        file_type,
        file_size,
        tag_list,
        attachment_size,
        modified_metadata,
        modified_attachment,
      } = image;

      formData.set('attachment', attachment);
      formData.set('modified_attachment', modified_attachment as any);
      formData.set('modified_metadata', modified_metadata as any);
      formData.set('attachment_size', attachment_size ? attachment_size.toString() : '');
      formData.append('file_name', file_name);
      formData.append('file_type', file_type);
      formData.set('file_size', file_size);
      formData.set('file_height', file_height);
      formData.set('file_width', file_width);
      formData.append('tag_list', tag_list);
      formData.append('media_bin', 'true');
    } else {
      formData.set('attachment', data);
      formData.append('file_name', data.name);
      formData.append('file_type', `${type === 'Videos' ? 'video' : type === 'Audio' ? 'audio' : 'document'}`);
      formData.set('file_size', data.size.toString());
      formData.append('media_bin', 'true');
    }

    const uploadedPhoto = await uploadMediaMutateAsync({ formData, onProgres });
    if (uploadedPhoto) {
      setuploading(false);
      setToDone(id);
      queryClient.invalidateQueries(['media']);
      queryClient.invalidateQueries(['profile_media'], { refetchInactive: true });
      const queries = queryClient
        .getQueryCache()
        .getAll()
        .filter((query) => {
          const key = query.queryKey;

          return (
            Array.isArray(key) &&
            key.length === 3 && // Assuming the key has exactly three elements
            key[0] === REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media &&
            key[2] === types[type]
          );
        });

      queries.map((queryKey) =>
        queryClient.invalidateQueries(queryKey.queryKey, {
          refetchInactive: true,
        }),
      );
    } else {
      console.error('Uploading Failed');
    }
  };

  useEffect(() => {
    // setuploading(true);
    // setTimeout(() => {
    //   setuploading(false);
    //   setToDone(id);
    // }, 2000);
    if (type === 'Images') {
      handleUpload([], file);
    } else {
      handleUpload(file, []);
    }
  }, [file]);

  const progressOverlay = () => (
    <ProgressOverlay>
      <svg width="0" height="0">
        <linearGradient id="linearColors" x1="0" y1="0" x2="1" y2="1">
          <stop offset="20%" stopColor={COLORS.PRIMARY} />
          <stop offset="90%" stopColor={COLORS.PRIMARY} />
        </linearGradient>
      </svg>
      {progress === 100 ? (
        <CircularProgress thickness={3} classes={{ circle: classes.circle }} style={{ width: 30, height: 30 }} />
      ) : (
        <>
          <CircularProgress
            thickness={3}
            variant="determinate"
            value={progress}
            classes={{ circle: classes.circle }}
            style={{ width: 30, height: 30 }}
          />
          <p style={{ position: 'absolute', fontSize: 10 }}>{`${Math.round(progress)}%`}</p>
        </>
      )}
    </ProgressOverlay>
  );

  const renderIcon = () => {
    switch (type) {
      case 'Images':
        return <ColoredImageIcon style={{ width: 20, height: 20, marginRight: 10, alignSelf: 'center' }} />;
      case 'Videos':
        return <VideoIcon style={{ width: 20, height: 20, marginRight: 10, alignSelf: 'center' }} />;
      case 'Audio':
        return <MicrophoneIcon style={{ width: 20, height: 20, marginRight: 10, alignSelf: 'center' }} />;
      default:
        return <DocumentFileIcon className="documentIcon" style={{ height: 20, marginRight: 10, width: 18 }} />;
    }
  };

  return (
    <>
      <Grid item className={classes.cardItem}>
        <Grid item>{renderIcon()}</Grid>
        <p
          style={{
            fontWeight: 500,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flex: 1,
            marginRight: 20,
            fontSize: 15,
          }}
        >
          {label}
        </p>
        <Grid item className={classes.buttonContainer}>
          {uploading ? (
            progressOverlay()
          ) : (
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <Typography color="warning" style={{ margin: 5 }} fontSize={13}>
                Upload Complete
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                className={classes.buttonCls}
                onClick={() => {
                  removeMe(id);
                }}
              >
                Clear
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default UploadingItem;
